import { FormActionTypeEnum } from '@finxone-platform/form-action';
import {
  NotificationTransactionStatus,
  NotificationTypeEnum,
} from '@finxone-platform/shared/sys-config-types';
import { Store } from '@ngxs/store';
import { UpdateFormDataActionWithId } from '../../actions/form-submission.action';
import { AccountService } from '../../services/account-service/account-service.service';
import { getPaymentRequestStatusNotificationDataAndRedirect } from './payment-request-notification/payment-request-notification.utils';

export interface NotificationActionResources {
  status: NotificationTransactionStatus;
  notificationId: string;
  store: Store;
  accountService: AccountService;
  authenticationId?: string;
  requestId?: string;
  recordId?: string;
  invitationUrl?: string;
  notificationUrl?: string;
}

export const NotificationCenterActionsMap: Map<
  NotificationTypeEnum,
  (resources: NotificationActionResources) => string
> = new Map<NotificationTypeEnum, any>([
  [
    NotificationTypeEnum.TRANSACTION_OUTGOING,
    (resources: NotificationActionResources) => {
      return getOutgoingTransactionDestinationURL(resources);
    },
  ],
  [
    NotificationTypeEnum.TRANSACTION_INCOMING,
    (resources: NotificationActionResources) => {
      return getIncomingTransactionDestinationURL(resources);
    },
  ],
  [
    NotificationTypeEnum.EXCHANGE_COMPLETED,
    (resources: NotificationActionResources) => {
      return getExchangeDestinationURL(resources);
    },
  ],
  [
    NotificationTypeEnum.EXCHANGE_FAILED,
    (resources: NotificationActionResources) => {
      return getExchangeDestinationURL(resources);
    },
  ],
  [
    NotificationTypeEnum.DAILY_BALANCE,
    (resources: NotificationActionResources) => {
      return '/zones/profile/notification-balance/' + resources.notificationId;
    },
  ],
  [
    NotificationTypeEnum.PAYMENT_REQUEST,
    (resources: NotificationActionResources) => {
      return getPaymentRequestStatusNotificationDataAndRedirect(resources);
    },
  ],
  [
    NotificationTypeEnum.PAYMENT_REQUEST_APPROVED,
    (resources: NotificationActionResources) => {
      return getPaymentRequestStatusNotificationDataAndRedirect(resources);
    },
  ],
  [
    NotificationTypeEnum.PAYMENT_REQUEST_REJECTED,
    (resources: NotificationActionResources) => {
      return getPaymentRequestStatusNotificationDataAndRedirect(resources);
    },
  ],
  [
    NotificationTypeEnum.BENEFICIARY_ADDED,
    (resources: NotificationActionResources) => {
      return getBeneficiaryAddedDestinationURL(resources);
    },
  ],
  [
    NotificationTypeEnum.BENEFICIARY_UPDATED,
    (resources: NotificationActionResources) => {
      return getBeneficiaryEditedDestinationURL(resources);
    },
  ],
  [
    NotificationTypeEnum.BENEFICIARY_REMOVED,
    (resources: NotificationActionResources) => {
      return getBeneficiaryRemovedDestinationURL(resources);
    },
  ],
  [
    NotificationTypeEnum.BULK_PAYMENT_REQUEST,
    (resources: NotificationActionResources) => {
      return getBulkPaymentRequestDestinationURL(resources);
    },
  ],
  [
    NotificationTypeEnum.BULK_PAYMENT_REQUEST_APPROVAL,
    (resources: NotificationActionResources) => {
      console.log(resources);
      return getBulkPaymentRequestDestinationURL(resources);
    },
  ],
  [
    NotificationTypeEnum.KYC_APPLICATION_ACCEPTED,
    (resources: NotificationActionResources) => {
      return kycApplicationSuccessNotificationRedirect(resources);
    },
  ],
  [
    NotificationTypeEnum.APPLICATION_REJECTION,
    (resources: NotificationActionResources) => {
      return kycApplicationRejectionNotificationRedirect(resources);
    },
  ],
  [
    NotificationTypeEnum.KYC_RFI_ACTION_REQUIRED,
    (resources: NotificationActionResources) => resources.notificationUrl ?? '/airwallex-rfi',
  ],
  [
    NotificationTypeEnum.THREEDS_CHALLENGE,
    (resources: NotificationActionResources) => {
      const authenticationId = resources.authenticationId ?? resources.requestId;
      return authenticationId ? `/card-authentication-review/${authenticationId}` : '';
    },
  ],
  [
    NotificationTypeEnum.GENERIC_WORKFLOW,
    (resources: NotificationActionResources) => {
      return '/zones/profile/notification/' + resources.notificationId;
    },
  ],
  [
    NotificationTypeEnum.JOINT_ACCOUNT_INVITATION,
    (resources: NotificationActionResources) => {
      const url = new URL(resources.invitationUrl ?? '');
      return `${url.pathname}${url.search}`;
    },
  ],
]);

function getOutgoingTransactionDestinationURL(resources: NotificationActionResources): string {
  if (resources.status === NotificationTransactionStatus.SUCCESS) {
    return '/zones/profile/notification-transaction-success/' + resources.notificationId;
  } else if (resources.status === NotificationTransactionStatus.FAIL) {
    return '/zones/profile/notification-transaction-failed/' + resources.notificationId;
  } else {
    console.error('Transaction status not recognised:', resources.status);
    return '';
  }
}

function getIncomingTransactionDestinationURL(resources: NotificationActionResources): string {
  if (resources.status === NotificationTransactionStatus.SUCCESS) {
    return '/zones/profile/notification-deposit-arrived/' + resources.notificationId;
  } else {
    console.error('Transaction status not recognised:', resources.status);
    return '';
  }
}

function getExchangeDestinationURL(resources: NotificationActionResources): string {
  if (resources.status === NotificationTransactionStatus.SUCCESS) {
    return '/zones/profile/notification-exchange-success/' + resources.notificationId;
  } else if (resources.status === NotificationTransactionStatus.FAIL) {
    return '/zones/profile/notification-exchange-failed/' + resources.notificationId;
  } else {
    console.error('Transaction status not recognised:', resources.status);
    return '';
  }
}

function getBeneficiaryAddedDestinationURL(resources: NotificationActionResources): string {
  return '/zones/profile/notification-beneficiary-added/' + resources.notificationId;
}

function getBeneficiaryEditedDestinationURL(resources: NotificationActionResources): string {
  return '/zones/profile/notification-beneficiary-edited/' + resources.notificationId;
}

function getBeneficiaryRemovedDestinationURL(resources: NotificationActionResources): string {
  return '/zones/profile/notification-beneficiary-removed/' + resources.notificationId;
}

function getBulkPaymentRequestDestinationURL(resources: NotificationActionResources): string {
  resources.store.dispatch(
    new UpdateFormDataActionWithId(
      {
        masterFileId: resources['requestId'],
        recordId: resources['recordId'] ?? '',
      },
      FormActionTypeEnum.BULK_PAYMENTS,
    ),
  );
  return '/zones/payments/bulk-payment-summary-elaborated-view-approver-reject';
}

function kycApplicationSuccessNotificationRedirect(resources: NotificationActionResources): string {
  return '/zones/profile/notification-application-success/' + resources.notificationId;
}

function kycApplicationRejectionNotificationRedirect(resources: NotificationActionResources): string {
  return '/zones/profile/notification-application-rejection/' + resources.notificationId;
}
